import { LegalShell } from "@/components/legal/LegalShell";

const LAST_UPDATED = "October 12, 2026";

const SECTIONS = [
  {
    id: "agreement",
    title: "Your agreement with us",
    body: (
      <>
        <p>
          These Terms of Service (the "Terms") govern your use of Last Bookmark — the website,
          app, and any related services (together, the "Platform"). By creating an account, or
          by using the Platform in any way, you agree to these Terms.
        </p>
        <p>
          During the beta phase, the{" "}
          <a href="/beta-terms" className="link-underline">Beta Terms</a> also apply and take
          precedence where they cover the same subject (payments, feature stability, uptime).
        </p>
      </>
    ),
  },
  {
    id: "eligibility",
    title: "Who can use Last Bookmark",
    body: (
      <>
        <p>
          You must be <strong>at least 18 years old</strong> and legally able to enter into a
          binding contract to create an account. You must provide accurate information when
          you register and keep your login credentials confidential — you're responsible for
          activity on your account.
        </p>
        <p>
          One account per person, please. Duplicate accounts, ban-evasion accounts, and
          accounts created to harass or defraud other users may be suspended or removed.
        </p>
      </>
    ),
  },
  {
    id: "what-we-provide",
    title: "What Last Bookmark provides",
    body: (
      <>
        <p>
          Last Bookmark is a <strong>marketplace and communication platform</strong>. We give
          authors a place to post projects, give beta readers a place to apply, and give both
          sides tools to exchange manuscripts, messages, and feedback. We also provide profiles,
          reviews, coffee-chat introductions, and an optional Last Bookmark Certified Beta Reader designation.
        </p>
        <p>
          <strong>We are not a party to the arrangements you make with other users.</strong>{" "}
          The commercial or non-commercial relationship you form with another user — whether
          it's an unpaid swap, a fee-for-service beta read, or an ongoing critique-partner
          arrangement — is between you and them.
        </p>
      </>
    ),
  },
  {
    id: "payments-beta",
    title: "Payments during beta",
    body: (
      <>
        <p>
          <strong>Last Bookmark does not currently process, hold, or protect payments.</strong>
          There is no built-in payment method during the beta phase.
        </p>
        <p>
          Any payment arrangement between an author and a beta reader is made{" "}
          <strong>directly between them, at their own discretion and risk</strong>. Last
          Bookmark is not a party to those payments, does not collect fees on them, and does
          not verify either user's financial ability to perform.
        </p>
        <p>
          Because we don't process the payment, we <strong>cannot</strong> refund it, reverse
          it, hold it in escrow, or intervene in a payment dispute. If you send money to
          another user, you are trusting that user directly. Please read our{" "}
          <a href="/payment-guidance" className="link-underline">Payment guidance</a> before
          agreeing to any paid arrangement.
        </p>
        <p>
          <strong>Secure platform-managed payments through Stripe Connect are planned for a
          future update</strong>, but are not currently available. The exact payment flow,
          payout timing, protections, fees, and dispute process will be clearly disclosed
          inside the app before those features become available.
        </p>
      </>
    ),
  },
  {
    id: "your-content",
    title: "Your content and intellectual property",
    body: (
      <>
        <p>
          You keep <strong>full ownership</strong> of the manuscripts, feedback, profile
          content, messages, and everything else you create on the Platform. You grant Last
          Bookmark a limited, non-exclusive, worldwide, royalty-free licence to host, store,
          transmit, and display your content solely for the purpose of running the Platform on
          your behalf.
        </p>
        <p>
          You promise that the content you upload is either yours or that you have the rights
          to share it. Do not upload manuscripts, images, or other work that you do not own or
          have permission to share.
        </p>
        <p>
          <strong>Confidentiality is a two-way street.</strong> When another user shares a
          manuscript with you, you agree to treat it as confidential — see our{" "}
          <a href="/community-guidelines" className="link-underline">Community Guidelines</a>
          {" "}for the full expectation.
        </p>
      </>
    ),
  },
  {
    id: "acceptable-use",
    title: "Acceptable use",
    body: (
      <>
        <p>You agree not to:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Harass, threaten, dox, or discriminate against another user.</li>
          <li>Scam, defraud, or misrepresent yourself to other users.</li>
          <li>Copy, redistribute, publish, or use another user's manuscript or private content without their permission.</li>
          <li>As a beta reader, use AI to generate feedback, reports, or reviews, or share an author&rsquo;s manuscript, excerpts, or confidential project materials with AI tools or services — see{" "}
            <a href="/community-guidelines#beta-readers-no-ai" className="link-underline" data-testid="terms-link-beta-readers-no-ai">For beta readers: personal reading, no AI</a>.</li>
          <li>Solicit payments off-platform for the purpose of avoiding our reporting or moderation tools.</li>
          <li>Post spam, unrelated advertising, malware, or otherwise abusive content.</li>
          <li>Attempt to reverse-engineer, disrupt, or gain unauthorised access to the Platform.</li>
          <li>Use the Platform to violate any law or the intellectual property rights of others.</li>
        </ul>
      </>
    ),
  },
  {
    id: "suspension",
    title: "Suspension and account removal",
    body: (
      <>
        <p>
          Last Bookmark reserves the right to <strong>suspend or remove accounts</strong> that
          violate these Terms, our{" "}
          <a href="/community-guidelines" className="link-underline">Community Guidelines</a>,
          harass or scam other users, or create an unsafe environment. We may act on user
          reports, our own moderation, or safety concerns.
        </p>
        <p>
          Where it's safe and reasonable to do so, we'll give you notice and a chance to
          explain. In cases of ongoing harm, fraud, or clear violations, we may act
          immediately. If your account is removed, content you posted may also be removed.
        </p>
      </>
    ),
  },
  {
    id: "termination-by-you",
    title: "Closing your account",
    body: (
      <>
        <p>
          You can close your account at any time by emailing us at{" "}
          <a href="mailto:hello@lastbookmark.com" className="link-underline">
            hello@lastbookmark.com
          </a>
          . See our <a href="/privacy" className="link-underline">Privacy Policy</a> for what
          happens to your data when you close your account.
        </p>
      </>
    ),
  },
  {
    id: "disclaimers",
    title: "Disclaimers",
    body: (
      <>
        <p className="uppercase text-xs tracking-[0.14em] text-muted-foreground">
          Read this section carefully.
        </p>
        <p>
          The Platform is provided <strong>"as is" and "as available"</strong>, without
          warranties of any kind, express or implied. We do not warrant that the Platform will
          be uninterrupted, error-free, secure against all attacks, or that any content or
          feedback obtained on the Platform will meet your expectations.
        </p>
        <p>
          Last Bookmark does not endorse or verify individual users, their claims of experience
          or credentials, or the quality of their work. Last Bookmark Certified Beta Reader status
          is a Last Bookmark platform designation — not an industry credential — that reflects our
          best judgment based on the applicant&rsquo;s submission and history on the Platform. Last
          Bookmark does not independently verify every claim an applicant makes, and Certified
          status does not guarantee any particular level of service.
        </p>
      </>
    ),
  },
  {
    id: "limitation-of-liability",
    title: "Limitation of liability",
    body: (
      <>
        <p>
          To the fullest extent permitted by law, Last Bookmark and its operators, employees,
          and contractors will not be liable for any indirect, incidental, consequential,
          special, or punitive damages arising out of or related to your use of the Platform,
          including without limitation any damages arising from a payment arrangement made
          directly between users, any loss of data, or any conduct of another user.
        </p>
        <p>
          Where liability cannot be excluded by law, our total liability to you for any claim
          arising out of or related to the Platform is limited to <strong>the greater of one
          hundred U.S. dollars (US$100) or the fees you paid Last Bookmark in the twelve months
          preceding the claim</strong>. During the beta phase, we do not charge fees, so the
          practical cap is US$100.
        </p>
      </>
    ),
  },
  {
    id: "indemnity",
    title: "Indemnity",
    body: (
      <>
        <p>
          You agree to indemnify and hold Last Bookmark harmless from any claim, loss,
          liability, or expense (including reasonable legal fees) arising out of your content,
          your use of the Platform, your interactions with other users, or your breach of
          these Terms.
        </p>
      </>
    ),
  },
  {
    id: "governing-law",
    title: "Governing law and disputes",
    body: (
      <>
        <p>
          These Terms are governed by the laws of the <strong>State of Florida, United States
          of America</strong>, without regard to conflict-of-laws principles. You agree that
          the exclusive venue for any dispute arising out of or related to these Terms or the
          Platform is the state or federal courts located in Florida, and you consent to the
          jurisdiction of those courts.
        </p>
        <p>
          If any part of these Terms is found unenforceable, the rest of the Terms will remain
          in effect. A failure to enforce any provision is not a waiver of it.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to these Terms",
    body: (
      <>
        <p>
          We may update these Terms as the platform grows. Material updates will bump the "Last
          updated" date and, where appropriate, be surfaced inside the app so you have a chance
          to review them.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <>
        <p>
          Questions about these Terms? Email{" "}
          <a href="mailto:hello@lastbookmark.com" className="link-underline">
            hello@lastbookmark.com
          </a>
          .
        </p>
      </>
    ),
  },
];

export default function TermsOfService() {
  return (
    <LegalShell
      eyebrow="Legal"
      title="Terms of Service"
      intro="The rules of using Last Bookmark, in plain English. Please read carefully — you agree to these Terms when you create an account."
      lastUpdated={LAST_UPDATED}
      sections={SECTIONS}
      testid="terms-of-service"
    />
  );
}
